import React, { useEffect, useState, useCallback } from "react";
import {
  InvoiceDetails,
  commonText,
  statusBtn,
  tooltips
} from "../../../locales/en";
import {
  FaSyncAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaExclamation,
  FaPaperPlane
} from "react-icons/fa";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { fetchXeroWritebackApData } from "../../../redux/xerowritebackSlice";
import { formatDateWithDayjs, showToast } from "../../../utils/utils";
import {
  showToastType,
  FAILED,
  PENDING,
  SUCCESS,
  UUID_REGEX
} from "../../../utils/constants/appConstants";
import NestedList from "./NestedList";
import PropTypes from "prop-types";
import styles from "./../../../assets/scss/custom.module.scss";
import "./xerowriteback.scss";
import privateAxios from "../../../utils/axiosPrivate";
import {
  getXeroWritebackhandleResyncIdURL,
  getXeroWritebackUpdateURL
} from "../../../utils/constants/apiEndPoints";
import { Tooltip as ReactTooltip } from "react-tooltip";

const renderLogDetails = (log, details) => {
  if (log?.isAttempted === false) {
    return (
      <ul>
        <li>{InvoiceDetails?.xeroWriteBack?.infoMsg}</li>
      </ul>
    );
  }

  if (log?.isAttempted === true && log?.successDetails !== null) {
    return <NestedList data={details} />;
  }

  return (
    <ul>
      <li>{log?.errorDetails}</li>
    </ul>
  );
};

const XeroWriteBack = (props) => {
  const { invData } = props;
  const { id } = useParams();
  const dispatch = useDispatch();
  const [openLogs, setOpenLogs] = useState(new Set());
  const [grpStatus, setGrpStatus] = useState("");
  const [adminWriteBackUuid, setAdminWriteBackUuid] = useState("");
  const [writeBackIdError, setWriteBackIdError] = useState("");
  const [loadSpin, setLoadSpin] = useState(false);
  const { xeroWritebackData } = useSelector(
    (state) => state.adminXeroWriteBack
  );

  const formatUuid = useCallback((value) => {
    const str = (value || "").toString();
    const raw = str.replace(/[-\s]/g, "").slice(0, 32);
    const parts = [
      raw.slice(0, 8),
      raw.slice(8, 12),
      raw.slice(12, 16),
      raw.slice(16, 20),
      raw.slice(20, 32)
    ].filter(Boolean);
    return parts.join("-");
  }, []);
  const uuidRegex = UUID_REGEX;

  const handleWriteBackChange = (e) => {
    const rawValue = e?.target?.value || "";
    setAdminWriteBackUuid(formatUuid(rawValue));
    setWriteBackIdError("");
  };

  const validateUuid = useCallback(() => {
    const trimmed = (
      typeof adminWriteBackUuid === "string" ? adminWriteBackUuid : ""
    ).trim();
    if (!trimmed) {
      setWriteBackIdError(InvoiceDetails?.xeroWriteBack?.writeBackIdError);
      return false;
    }
    if (!uuidRegex.test(trimmed)) {
      setWriteBackIdError(InvoiceDetails?.xeroWriteBack?.writeBackUUIdError);
      return false;
    }
    return true;
  }, [adminWriteBackUuid]);

  const handleUpdateId = useCallback(async () => {
    if (!validateUuid()) return;
    try {
      setLoadSpin(true);
      const payload = {
        quoteId: Number(id),
        xeroInvoiceId: adminWriteBackUuid
      };
      const response = await privateAxios.put(
        getXeroWritebackUpdateURL,
        payload
      );
      if (response.status === 200) {
        if (response?.data?.data?.status === false) {
          showToast(response?.data?.data?.message, showToastType?.error);
        } else {
          showToast(response?.data?.data?.message, showToastType?.success);
        }
        setWriteBackIdError("");
      }
    } catch (error) {
      showToast(error?.response?.data?.message, showToastType?.error);
    } finally {
      setLoadSpin(false);
    }
  }, [id, adminWriteBackUuid, validateUuid]);

  const handleResyncId = useCallback(async () => {
    try {
      setLoadSpin(true);
      const response = await privateAxios.post(
        getXeroWritebackhandleResyncIdURL(id)
      );
      if (response.status === 200) {
        showToast(response?.data?.data, showToastType?.success);
      }
    } catch (error) {
      showToast(error?.response?.data?.message, showToastType?.error);
    } finally {
      setLoadSpin(false);
    }
  }, [id]);

  const toggleDescription = useCallback((logId) => {
    setOpenLogs((prev) => {
      const next = new Set(prev);
      next.has(logId) ? next.delete(logId) : next.add(logId);
      return next;
    });
  }, []);

  useEffect(() => {
    dispatch(fetchXeroWritebackApData({ id })).unwrap();
  }, [id, dispatch]);

  useEffect(() => {
    if (invData) {
      setGrpStatus(invData?.quoteStatus?.id);
      setAdminWriteBackUuid(invData?.adminWriteBackUuid);
    }
  }, [invData]);

  const tooltipsData = [
    {
      id: InvoiceDetails?.xeroWriteBack?.refreshTooltipId,
      content: tooltips?.refreshWriteback
    },
    {
      id: InvoiceDetails?.xeroWriteBack?.resyncTooltipId,
      content: tooltips?.resyncWriteback
    }
  ];

  return (
    <>
      <div className={styles.titleArea}>
        <h5>{InvoiceDetails?.xeroWriteBack?.title}</h5>
      </div>
      <div className="row">
        <div className={`col-12 ${writeBackIdError ? "mb-5" : "mb-3"}`}>
          <div className="blog-input-wrap">
            <div className={`${styles.labelTitle} border-bottom`}>
              <h5>{InvoiceDetails?.xeroWriteBack?.writeBackStatus}</h5>
            </div>
          </div>
          <div className="inputtextfield d-flex">
            {grpStatus === 1 ? (
              <ul
                className={`${styles.btngroupwrap} d-flex justify-content-end align-items-center`}
              >
                <li>
                  <button
                    type="button"
                    className={`${styles.btnPrimary} edit-icon added-icon ms-0 me-1 red-bg red-bg-nohover`}
                  >
                    <FaTimesCircle />
                  </button>
                </li>
              </ul>
            ) : (
              <>
                <ul
                  className={`${styles.btngroupwrap} d-flex justify-content-end align-items-center writback-btn-group`}
                >
                  <li>
                    {xeroWritebackData?.status === "Failed" && (
                      <button
                        type="button"
                        className={`${styles.btnPrimary} edit-icon added-icon ms-0 me-1 red-bg red-bg-nohover`}
                      >
                        <FaTimesCircle />
                      </button>
                    )}
                    {xeroWritebackData?.status === SUCCESS && (
                      <button
                        type="button"
                        className={`${styles.btnPrimary} edit-icon added-icon ms-0 me-1 no-hover`}
                      >
                        <FaCheckCircle />
                      </button>
                    )}
                    {xeroWritebackData?.status === PENDING && (
                      <button
                        type="button"
                        className={`${styles.btnPrimary} edit-icon added-icon ms-0 me-1 bg-warning`}
                      >
                        <FaExclamation />
                      </button>
                    )}
                  </li>
                </ul>
                <div
                  className={`${styles.textfieldWrap} inputtextfield flex-grow-1 position-relative`}
                >
                  <input
                    type="text"
                    name="writeBack"
                    className={styles.textfield}
                    placeholder={InvoiceDetails?.xeroWriteBack?.xeroPlaceholder}
                    disabled={xeroWritebackData?.status !== FAILED}
                    value={adminWriteBackUuid || ""}
                    onChange={handleWriteBackChange}
                  />
                  {writeBackIdError && (
                    <div className="text-danger small d-block writeBackError">
                      {writeBackIdError}
                    </div>
                  )}
                </div>

                {xeroWritebackData?.status === FAILED && (
                  <ul
                    className={`${styles.btngroupwrap} d-flex justify-content-end align-items-center`}
                  >
                    <li>
                      <button
                        type="button"
                        className={`${styles.btnPrimary} edit-icon added-icon`}
                        onClick={handleUpdateId}
                        data-tooltip-id={
                          InvoiceDetails?.xeroWriteBack?.refreshTooltipId
                        }
                      >
                        {loadSpin ? (
                          <span className="loadSpin">
                            <FaSpinner />
                          </span>
                        ) : (
                          <FaPaperPlane />
                        )}
                      </button>
                    </li>
                  </ul>
                )}
              </>
            )}

            <ul
              className={`${styles.btngroupwrap} d-flex justify-content-end align-items-center`}
            >
              <li>
                <button
                  type="button"
                  className={`${styles.btnPrimary} edit-icon added-icon`}
                  onClick={handleResyncId}
                  data-tooltip-id={
                    InvoiceDetails?.xeroWriteBack?.resyncTooltipId
                  }
                >
                  {loadSpin ? (
                    <span className="loadSpin">
                      <FaSpinner />
                    </span>
                  ) : (
                    <FaSyncAlt />
                  )}
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div className="col-12 mb-3">
          <div className="blog-input-wrap">
            <div className={`${styles.labelTitle}`}>
              <h5>{InvoiceDetails?.xeroWriteBack?.writeBackLogs}</h5>
            </div>

            {grpStatus === 1 ? (
              <div
                className={`${styles.labelTitle} admin-xero-logs border-bottom`}
              />
            ) : (
              <div
                className={`${styles.labelTitle} admin-xero-logs border-bottom`}
              >
                {Array.isArray(xeroWritebackData?.writebacks) &&
                  xeroWritebackData.writebacks.length > 0 &&
                  xeroWritebackData.writebacks.map((log) => {
                    const isOpen = openLogs.has(log.id);
                    let details = {};
                    try {
                      if (
                        log?.isAttempted &&
                        log?.errorDetails === null &&
                        log?.successDetails !== null
                      ) {
                        details = JSON.parse(log?.successDetails || "{}");
                      } else {
                        details = JSON.parse(log?.errorDetails || "{}");
                      }
                    } catch {
                      // handle catch here;
                    }
                    return (
                      <div className={`xerologs-section`} key={log?.id}>
                        <div className="xero-logs-title d-flext align-items-start">
                          {log?.isAttempted &&
                            log?.errorDetails === null &&
                            log?.successDetails !== null && (
                              <span className="badge bg-success me-1">
                                {statusBtn?.Success}
                              </span>
                            )}

                          {log?.isAttempted === false && (
                            <span className="badge bg-info me-1">
                              {statusBtn?.Pending}
                            </span>
                          )}

                          {log?.isAttempted &&
                            log?.errorDetails !== null &&
                            log?.successDetails === null && (
                              <span className="badge bg-danger me-1">
                                {statusBtn?.Failed}
                              </span>
                            )}
                          <span className="date-wrap">
                            <strong>
                              {InvoiceDetails?.xeroWriteBack?.Created}
                            </strong>
                            {log?.createdAt && (
                              <>
                                {formatDateWithDayjs(
                                  log?.createdAt,
                                  "DD-MMM-YYYY HH:mm:ss"
                                )}
                              </>
                            )}
                            ,
                            <strong>
                              {InvoiceDetails?.xeroWriteBack?.Executed}
                            </strong>
                            {log?.createdAt && (
                              <>
                                {formatDateWithDayjs(
                                  log?.updatedAt,
                                  "DD-MMM-YYYY HH:mm:ss"
                                )}
                              </>
                            )}
                          </span>
                        </div>
                        <div
                          className={`xero-logs-content mt-2 mb-2 ${
                            isOpen ? "" : "show-description"
                          }`}
                        >
                          {renderLogDetails(log, details)}
                        </div>
                        <button
                          type="button"
                          className="show-desc-content mt-2"
                          onClick={() => toggleDescription(log.id)}
                        >
                          {isOpen
                            ? `${commonText?.seeLess}`
                            : `${commonText?.seeMore}`}
                        </button>
                        <div className="clearfix" />
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      </div>
      {tooltipsData.map(({ id, content }) => (
        <ReactTooltip
          key={id}
          className="tooltipcustom"
          id={id}
          place={InvoiceDetails?.xeroWriteBack?.tooltipPlace}
          content={content}
        />
      ))}
    </>
  );
};

XeroWriteBack.propTypes = {
  invData: PropTypes.object
};

export default XeroWriteBack;
